import type { PaymentSession, PaymentSessionStatus } from '../types';
import { POLLABLE_STATUSES, TERMINAL_STATUSES } from '../types';

export type PollerOptions = {
  intervalMs?: number;
  hiddenIntervalMs?: number;
  onUpdate: (session: PaymentSession) => void;
  onError?: (error: unknown) => void;
  shouldPoll?: (session: PaymentSession) => boolean;
};

export class SessionPoller {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private stopped = false;

  constructor(
    private readonly fetchSession: () => Promise<PaymentSession>,
    private readonly options: PollerOptions,
  ) {}

  start(initialSession?: PaymentSession): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.stopped = false;
    if (initialSession) {
      this.options.onUpdate(initialSession);
      if (!this.shouldContinue(initialSession)) {
        this.stop();
        return;
      }
    }
    void this.tick();
  }

  stop(): void {
    this.stopped = true;
    this.running = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private shouldContinue(session: PaymentSession): boolean {
    if (this.options.shouldPoll) {
      return this.options.shouldPoll(session);
    }
    return POLLABLE_STATUSES.includes(session.status);
  }

  private intervalMs(): number {
    const base = this.options.intervalMs ?? 3000;
    const hidden = this.options.hiddenIntervalMs ?? 10000;
    return typeof document !== 'undefined' && document.hidden ? hidden : base;
  }

  private scheduleNext(): void {
    if (this.stopped) {
      return;
    }
    this.timer = setTimeout(() => {
      void this.tick();
    }, this.intervalMs());
  }

  private async tick(): Promise<void> {
    if (this.stopped) {
      return;
    }
    try {
      const session = await this.fetchSession();
      this.options.onUpdate(session);
      if (!this.shouldContinue(session)) {
        this.stop();
        return;
      }
    } catch (error) {
      this.options.onError?.(error);
    }
    this.scheduleNext();
  }
}

export function isTerminalStatus(status: PaymentSessionStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function phaseFromSession(status: PaymentSessionStatus): import('../types').CheckoutPhase {
  switch (status) {
    case 'CREATED':
      return 'choose-method';
    case 'AWAITING_PAYMENT':
      return 'awaiting';
    case 'COMPLETED':
      return 'success';
    case 'EXPIRED':
      return 'expired';
    case 'CANCELLED':
      return 'cancelled';
    case 'FAILED':
      return 'failed';
    default:
      return 'error';
  }
}

export function msUntilExpiry(expiresAt: string): number {
  return Math.max(0, new Date(expiresAt).getTime() - Date.now());
}
